<section
  class="notice-fidelity notice-fidelity--workbench"
  aria-labelledby="notice-fidelity-heading"
>
  <div class="workstation-chrome">
    <h2 id="notice-fidelity-heading" class="visually-hidden">
      Notice fidelity
    </h2>
    @if (notice) {
      <p class="visually-hidden" role="status">{{ notice }}</p>
    }
    @if (loading || (error && !diagnostics.length)) {
      <div class="workstation-feedback">
        @if (loading) {
          <ui-standard-state
            kind="loading"
            message="Loading notice proof contract"
          />
        }
        @if (error && !diagnostics.length) {
          <p role="alert">{{ error }}</p>
        }
      </div>
    }
    @if (catalog; as cat) {
      <div class="toolbar">
        <label
          >Scope<select
            uiControl
            name="notice-scope"
            [ngModel]="scopeKey"
            (ngModelChange)="changeScope($event)"
            [disabled]="busy || loading"
          >
            <option value="">Choose an authorized scope</option>
            @for (item of cat.scopes; track scopeValue(item)) {
              <option [value]="scopeValue(item)">{{ item.label }}</option>
            }
          </select></label
        >
        <label
          >Example<select
            uiControl
            name="notice-example"
            aria-label="Synthetic example"
            [ngModel]="exampleCode"
            (ngModelChange)="changeExample($event)"
            [disabled]="busy || loading"
          >
            @for (example of cat.examples; track example.code) {
              <option [value]="example.code">{{ example.label }}</option>
            }
          </select></label
        >
      </div>
      @if (scope) {
        @if (localRecovery; as recovery) {
          <div
            class="local-recovery"
            role="region"
            aria-label="Saved-work recovery"
          >
            <p>
              {{
                recovery === 'available'
                  ? 'A local save is available for this scope.'
                  : 'A local save exists but is incompatible or unreadable. Saving will ask before replacing it.'
              }}
            </p>
            @if (recovery === 'available') {
              <button
                uiButton="primary"
                type="button"
                (click)="reloadLocal()"
                [disabled]="!canEdit"
              >
                Resume saved work
              </button>
            }
          </div>
        }
        <div class="actions" aria-label="Composition actions">
          <button
            uiButton="primary"
            type="button"
            (click)="save()"
            [disabled]="!canSave"
          >
            Save locally
          </button>
          <button
            uiButton="secondary"
            type="button"
            (click)="saveAndPreview()"
            [disabled]="!canSaveAndPreview"
          >
            {{ exactSaved ? 'Preview PDF' : 'Save & preview' }}
          </button>
          <details
            #moreActions
            (focusout)="closeToolsAfterFocus($event)"
            class="more-actions"
            (keydown.escape)="
              closeMoreActions();
              $event.preventDefault();
              $event.stopPropagation()
            "
          >
            <summary #moreSummary>More actions</summary>
            <div class="more-actions-panel">
              <button
                uiButton="secondary"
                type="button"
                (click)="closeMoreActions(); loadCatalog()"
                [disabled]="busy || loading || !canRead()"
              >
                Refresh access
              </button>
              <button
                uiButton="secondary"
                type="button"
                (click)="closeMoreActions(); exportSource()"
                [disabled]="!exactSaved || busy"
              >
                Export JSON
              </button>
              <button
                uiButton="secondary"
                type="button"
                (click)="closeMoreActions(); reloadLocal()"
                [disabled]="!canEdit || !hasLocalSave"
              >
                Reload local save
              </button>
              <button
                uiButton="secondary"
                type="button"
                (click)="closeMoreActions(); resetExample()"
                [disabled]="!canEdit"
              >
                Load starter
              </button>
              @if (cat.documentContext && cat.documentSourceJson) {
                <button
                  uiButton="secondary"
                  type="button"
                  (click)="closeMoreActions(); useDocumentContext()"
                  [disabled]="!canEdit"
                >
                  Use registered document context
                </button>
              }
              <button
                uiButton="secondary"
                type="button"
                (click)="closeMoreActions(); fileInput.click()"
                [disabled]="!canEdit"
              >
                Import JSON
              </button>
              <input
                #fileInput
                class="file-input"
                type="file"
                accept=".json,application/json"
                aria-label="Import notice composition JSON"
                (change)="importFile($event)"
              />
              <details>
                <summary>Supported proof boundary</summary>
                <p>
                  Synthetic · browser-local saves · no publication or delivery
                </p>
                <p>
                  {{ cat.rendererCode }} · {{ cat.rendererVersion }} ·
                  {{ cat.contractCode }} v{{ cat.contractVersion }}
                </p>
                <ul>
                  @for (item of cat.unsupported; track $index) {
                    <li>{{ item }}</li>
                  }
                </ul>
              </details>
            </div>
          </details>
          @if (busy) {
            <button uiButton="secondary" type="button" (click)="cancel()">
              Cancel request
            </button>
          }
        </div>
        <nav class="view-actions" aria-label="Notice editor view">
          <button
            uiButton="secondary"
            type="button"
            [attr.aria-pressed]="view === 'page'"
            (click)="
              pageFocusBlockOnOpen = null; pageIssueFocus = null; view = 'page'
            "
          >
            Page
          </button>
          <button
            uiButton="secondary"
            type="button"
            [attr.aria-pressed]="view === 'fields'"
            (click)="view = 'fields'"
          >
            Fields
          </button>
          <button
            uiButton="secondary"
            type="button"
            [attr.aria-pressed]="view === 'preview'"
            (click)="view = 'preview'"
          >
            Preview
          </button>
          <button
            uiButton="secondary"
            type="button"
            [attr.aria-pressed]="view === 'review'"
            (click)="view = 'review'"
          >
            Review changes
          </button>
          @if (view !== 'page' || !composition) {
            <button
              uiButton="secondary"
              type="button"
              (click)="historyCommand('undo')"
              [disabled]="!canEdit || !history.canUndo"
            >
              Undo
            </button>
            <button
              uiButton="secondary"
              type="button"
              (click)="historyCommand('redo')"
              [disabled]="!canEdit || !history.canRedo"
            >
              Redo
            </button>
          }
          <span class="workspace-kind">Synthetic · browser local</span>
          <span class="state-label">{{
            busy
              ? 'Working…'
              : dirty
                ? 'Unsaved composition'
                : exactSaved
                  ? 'Saved locally'
                  : localRecovery === 'available'
                    ? 'Local save available'
                    : 'No verified save open'
          }}</span>
        </nav>
      }
    }
  </div>
  <div class="workstation-body" role="region" aria-label="Notice working area">
    @if (catalog; as cat) {
      @if (!cat.scopes.length) {
        <ui-standard-state
          kind="empty"
          message="Notice editing requires the Read content templates and Author content templates permissions. Ask an administrator to grant these permissions in Access management."
        />
      } @else if (scope) {
        @if (diagnostics.length) {
          <phms-notice-diagnostics
            class="diagnostics-panel"
            [diagnostics]="diagnostics"
            [composition]="composition"
            [catalog]="cat"
            [disabled]="!canEdit"
            (navigate)="navigateIssue($event)"
          />
        }
        @if (view === 'review') {
          <div
            class="review-scroll"
            role="region"
            aria-label="Notice change review"
            tabindex="0"
          >
            <phms-notice-change-review
              [baseline]="baseline"
              [baselineDiagnostics]="baselineDiagnostics"
              [composition]="composition"
              [currentSourceHash]="exactSaved ? saved!.sourceHash : null"
              [comparison]="comparison"
              [proofs]="exactComparisonProofs"
              [canCapture]="canCaptureBaseline"
              [canUndoBaseline]="canUndoBaseline"
              [canRender]="canRenderComparison"
              [disabled]="!canEdit"
              (captureBaseline)="captureBaseline()"
              (clearBaseline)="clearBaseline()"
              (undoBaseline)="undoBaselineChange()"
              (renderComparison)="renderComparison()"
              (editBlock)="editComparedBlock($event)"
              (downloadProof)="exportComparisonPdf($event)"
              (fieldsRequested)="view = 'fields'"
            />
          </div>
        }
        <div class="workspace" [hidden]="view === 'review'">
          @if (view === 'page') {
            @if (composition; as pageDocument) {
              <phms-notice-page-editor
                [assetCatalog]="assetCatalog"
                [assetsLoading]="assetsLoading"
                [assetsError]="assetsError"
                [brandProfile]="brandProfile"
                [brandLogoUrl]="brandLogoUrl"
                [brandError]="brandError"
                (loadAssets)="loadAssets()"
                [composition]="pageDocument"
                [catalog]="cat"
                [selectedBlockId]="selectedBlockId"
                [focusBlockOnOpen]="pageFocusBlockOnOpen"
                [focusIssueOnOpen]="pageIssueFocus"
                [viewIdentity]="pageViewIdentity"
                [viewState]="pageViewState"
                [scopeIdentity]="scopeKey"
                [disabled]="!canEdit"
                [canUndo]="history.canUndo"
                [canRedo]="history.canRedo"
                [proof]="exactProof ? proof : null"
                (documentChange)="pageChanged($event)"
                (selectedBlockIdChange)="selectPageBlock($event)"
                (viewStateChange)="rememberPageView($event)"
                (historyCommand)="historyCommand($event)"
                (breakHistoryGroup)="history.breakGroup()"
                (fieldsRequested)="view = 'fields'"
                (accessLost)="pageAccessLost()"
                (issueFocusUnavailable)="issueFocusUnavailable()"
              />
            } @else {
              <p>
                Save locally to validate this source and resume page editing.
                <button
                  uiButton="secondary"
                  type="button"
                  (click)="view = 'fields'"
                >
                  Open Fields
                </button>
              </p>
            }
          }
          <div
            class="editor"
            [hidden]="view !== 'fields'"
            role="region"
            aria-label="Notice fields and source"
            tabindex="0"
          >
            @if (usingDocumentContext && cat.documentContext; as context) {
              <details class="context-catalog">
                <summary>
                  {{ context.label }} · v{{ context.version }} · fields and
                  diagnostics
                </summary>
                <p class="muted">
                  Synthetic values only. Business data and agency terminology
                  resolve separately. A selected unavailable value withholds the
                  complete proof.
                </p>
                <dl>
                  @for (field of context.fields; track field.code) {
                    <dt>{{ field.label }} · {{ field.code }}</dt>
                    <dd>
                      {{ field.dataType }} ·
                      {{ field.required ? 'Required' : 'Optional' }} ·
                      {{ field.classification }} ·
                      {{ missingBehaviorLabel(field.missingBehavior)
                      }}<br />Formats: {{ field.formats.join(', ') }}
                    </dd>
                  }
                  @for (list of context.lists; track list.code) {
                    <dt>{{ list.label }} · {{ list.code }}</dt>
                    <dd>
                      {{ list.required ? 'Required' : 'Optional' }} list · up to
                      {{ list.maxItems }} rows
                    </dd>
                  }
                  @for (term of context.terms; track term.code) {
                    <dt>{{ term.label }} · {{ term.code }}</dt>
                    <dd>
                      Terminology · {{ term.tokenCode }} ·
                      {{ term.configurationCode }}<br />Declared fallback:
                      {{ term.fallbackText }}
                    </dd>
                  }
                </dl>
                <p class="muted">Context SHA-256: {{ context.hash }}</p>
              </details>
            }
            @if (composition; as doc) {
              <fieldset [disabled]="!canEdit">
                <legend>Composition</legend>
                <phms-notice-document-settings
                  [assetCatalog]="assetCatalog"
                  [assetsLoading]="assetsLoading"
                  [assetsError]="assetsError"
                  (loadAssets)="loadAssets()"
                  [composition]="doc"
                  [catalog]="cat"
                  [disabled]="!canEdit"
                  (setting)="documentSetting($event)"
                  (breakHistoryGroup)="history.breakGroup()"
                />
                <div class="block-list">
                  @for (block of doc.blocks; track $index; let index = $index) {
                    <article
                      class="block"
                      [attr.aria-label]="'Block ' + (index + 1)"
                    >
                      <div class="block-heading">
                        <strong
                          >{{ index + 1 }} ·
                          {{
                            block.kind === 'pageBreak'
                              ? 'Page break'
                              : block.kind
                          }}</strong
                        >
                        <div class="actions">
                          <button
                            uiButton="secondary"
                            type="button"
                            [attr.aria-label]="
                              'Move block ' + (index + 1) + ' up'
                            "
                            (click)="move(index, -1)"
                            [disabled]="index === 0"
                          >
                            Up
                          </button>
                          <button
                            uiButton="secondary"
                            type="button"
                            [attr.aria-label]="
                              'Move block ' + (index + 1) + ' down'
                            "
                            (click)="move(index, 1)"
                            [disabled]="index === doc.blocks.length - 1"
                          >
                            Down
                          </button>
                          <button
                            uiButton="secondary"
                            type="button"
                            [attr.aria-label]="'Remove block ' + (index + 1)"
                            (click)="removeBlock(index)"
                            [disabled]="doc.blocks.length === 1"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                      <label
                        >Block ID<input
                          uiControl
                          [name]="'block-id-' + index"
                          [(ngModel)]="block.id"
                          (ngModelChange)="edited()"
                      /></label>
                      @if (block.runs; as runs) {
                        @for (
                          run of runs;
                          track $index;
                          let runIndex = $index
                        ) {
                          <div class="run">
                            <label
                              >Run {{ runIndex + 1
                              }}<select
                                uiControl
                                [name]="'run-kind-' + index + '-' + runIndex"
                                [ngModel]="run.kind"
                                (ngModelChange)="
                                  runKind(block, runIndex, $event)
                                "
                              >
                                <option value="literal">Text</option>
                                <option value="field">Data field</option>
                                <option value="term">Terminology</option>
                              </select></label
                            >
                            @if (run.kind === 'literal') {
                              <label class="run-value"
                                >Text<textarea
                                  uiControl
                                  rows="2"
                                  [name]="'run-value-' + index + '-' + runIndex"
                                  [ngModel]="run.value"
                                  (ngModelChange)="
                                    runValue(block, runIndex, $event)
                                  "
                                ></textarea>
                              </label>
                            } @else {
                              <label class="run-value"
                                >{{
                                  run.kind === 'field' ? 'Data field' : 'Term'
                                }}<select
                                  uiControl
                                  [name]="'run-value-' + index + '-' + runIndex"
                                  [ngModel]="run.value"
                                  (ngModelChange)="
                                    runValue(block, runIndex, $event)
                                  "
                                >
                                  @if (!knownRunBinding(run.kind, run.value)) {
                                    <option [value]="run.value">
                                      Unavailable: {{ run.value }}
                                    </option>
                                  }
                                  @for (
                                    token of run.kind === 'field'
                                      ? fieldBindings
                                      : termBindings;
                                    track token.code
                                  ) {
                                    <option [value]="token.code">
                                      {{ token.label }} · {{ token.code }}
                                    </option>
                                  }
                                </select></label
                              >
                            }
                            @if (run.kind === 'field') {
                              @if (fieldDetails(run.value); as field) {
                                <label
                                  >Format<select
                                    uiControl
                                    [name]="
                                      'run-format-' + index + '-' + runIndex
                                    "
                                    [ngModel]="run.format ?? null"
                                    (ngModelChange)="
                                      runFormat(block, runIndex, $event)
                                    "
                                  >
                                    <option [ngValue]="null">
                                      Default ({{ field.formats[0] }})
                                    </option>
                                    @if (
                                      run.format !== undefined &&
                                      !field.formats.includes(run.format)
                                    ) {
                                      <option [value]="run.format">
                                        Unavailable: {{ run.format }}
                                      </option>
                                    }
                                    @for (
                                      format of field.formats;
                                      track format
                                    ) {
                                      <option [value]="format">
                                        {{ format }}
                                      </option>
                                    }
                                  </select></label
                                >
                                <p class="binding-guidance muted">
                                  {{ field.dataType }} ·
                                  {{ field.required ? 'Required' : 'Optional' }}
                                  ·
                                  {{
                                    missingBehaviorLabel(field.missingBehavior)
                                  }}
                                </p>
                              }
                            }
                            <label class="check"
                              ><input
                                type="checkbox"
                                [name]="'run-bold-' + index + '-' + runIndex"
                                [(ngModel)]="run.bold"
                                (ngModelChange)="edited()"
                              />Bold</label
                            >
                            <label class="check"
                              ><input
                                type="checkbox"
                                [name]="'run-italic-' + index + '-' + runIndex"
                                [(ngModel)]="run.italic"
                                (ngModelChange)="edited()"
                              />Italic</label
                            >
                            <button
                              uiButton="secondary"
                              type="button"
                              [attr.aria-label]="
                                'Remove run ' +
                                (runIndex + 1) +
                                ' from block ' +
                                (index + 1)
                              "
                              (click)="removeRun(block, runIndex)"
                              [disabled]="runs.length === 1"
                            >
                              Remove run
                            </button>
                          </div>
                        }
                        <button
                          uiButton="secondary"
                          type="button"
                          (click)="addRun(block)"
                          [disabled]="runs.length >= 64"
                        >
                          Add run
                        </button>
                      }
                      @if (block.columns; as columns) {
                        @if (usingDocumentContext) {
                          <label
                            >Repeated data<select
                              uiControl
                              [name]="'repeat-source-' + index"
                              [ngModel]="block.source"
                              (ngModelChange)="listSource(block, $event)"
                            >
                              @for (
                                list of cat.documentContext?.lists;
                                track list.code
                              ) {
                                <option [value]="list.code">
                                  {{ list.label }} · {{ list.code }}
                                </option>
                              }
                            </select></label
                          >
                          @if (listDetails(block); as list) {
                            <p class="muted">
                              {{ list.required ? 'Required' : 'Optional' }} list
                              · up to {{ list.maxItems }} rows. Synthetic rows
                              flow across pages; an empty list produces no
                              participant values.
                            </p>
                          }
                        } @else {
                          <p class="muted">
                            Repeats synthetic notice.items; rows flow across
                            pages.
                          </p>
                        }
                        @for (
                          column of columns;
                          track $index;
                          let columnIndex = $index
                        ) {
                          <div class="fields">
                            <label
                              >Column ID<input
                                uiControl
                                [name]="
                                  'column-id-' + index + '-' + columnIndex
                                "
                                [(ngModel)]="column.id"
                                (ngModelChange)="edited()"
                            /></label>
                            <label
                              >Heading<input
                                uiControl
                                [name]="
                                  'column-label-' + index + '-' + columnIndex
                                "
                                [(ngModel)]="column.label"
                                (ngModelChange)="edited()"
                            /></label>
                            <label
                              >Field<select
                                uiControl
                                [name]="
                                  'column-field-' + index + '-' + columnIndex
                                "
                                [ngModel]="column.field"
                                (ngModelChange)="
                                  columnField(block, columnIndex, $event)
                                "
                              >
                                @for (
                                  field of columnFields(block);
                                  track field.code
                                ) {
                                  <option [value]="field.code">
                                    {{ field.label }} · {{ field.code }}
                                  </option>
                                }
                              </select></label
                            >
                            @if (columnDetails(block, column.field); as field) {
                              <label
                                >Format<select
                                  uiControl
                                  [name]="
                                    'column-format-' + index + '-' + columnIndex
                                  "
                                  [(ngModel)]="column.format"
                                  (ngModelChange)="edited()"
                                >
                                  @for (format of field.formats; track format) {
                                    <option [value]="format">
                                      {{ format }}
                                    </option>
                                  }
                                </select></label
                              >
                              <p class="binding-guidance muted">
                                {{ field.dataType }} ·
                                {{ field.required ? 'Required' : 'Optional' }} ·
                                {{
                                  missingBehaviorLabel(field.missingBehavior)
                                }}
                              </p>
                            }
                            <label
                              >Width ratio<input
                                uiControl
                                type="number"
                                min="1"
                                max="4"
                                [name]="
                                  'column-width-' + index + '-' + columnIndex
                                "
                                [(ngModel)]="column.width"
                                (ngModelChange)="edited()"
                            /></label>
                            <button
                              uiButton="secondary"
                              type="button"
                              [attr.aria-label]="
                                'Remove column ' +
                                (columnIndex + 1) +
                                ' from block ' +
                                (index + 1)
                              "
                              (click)="removeColumn(block, columnIndex)"
                              [disabled]="columns.length <= 2"
                            >
                              Remove column
                            </button>
                          </div>
                        }
                        <button
                          uiButton="secondary"
                          type="button"
                          (click)="addColumn(block)"
                          [disabled]="columns.length >= 4"
                        >
                          Add column
                        </button>
                      }
                      @if (block.style; as style) {
                        <details>
                          <summary>Block appearance</summary>
                          <div class="fields">
                            <label
                              >Text size (pt)<input
                                uiControl
                                type="number"
                                min="8"
                                max="24"
                                [name]="'style-size-' + index"
                                [(ngModel)]="style.fontSizePt"
                                (ngModelChange)="edited()"
                            /></label>
                            <label
                              >Text color<input
                                uiControl
                                type="color"
                                [name]="'style-color-' + index"
                                [(ngModel)]="style.color"
                                (ngModelChange)="edited()"
                            /></label>
                            <label
                              >Fill<input
                                uiControl
                                type="color"
                                [name]="'style-fill-' + index"
                                [(ngModel)]="style.backgroundColor"
                                (ngModelChange)="edited()"
                            /></label>
                            <label
                              >Border color<input
                                uiControl
                                type="color"
                                [name]="'style-border-color-' + index"
                                [(ngModel)]="style.borderColor"
                                (ngModelChange)="edited()"
                            /></label>
                            <label
                              >Border (pt)<input
                                uiControl
                                type="number"
                                min="0"
                                max="3"
                                [name]="'style-border-' + index"
                                [(ngModel)]="style.borderWidthPt"
                                (ngModelChange)="edited()"
                            /></label>
                            <label
                              >Padding (pt)<input
                                uiControl
                                type="number"
                                min="0"
                                max="20"
                                [name]="'style-padding-' + index"
                                [(ngModel)]="style.paddingPt"
                                (ngModelChange)="edited()"
                            /></label>
                            <label class="check"
                              ><input
                                type="checkbox"
                                [name]="'style-keep-' + index"
                                [(ngModel)]="style.keepTogether"
                                (ngModelChange)="edited()"
                              />Keep block together</label
                            >
                          </div>
                        </details>
                      }
                    </article>
                  }
                </div>
                <div class="actions" aria-label="Add composition block">
                  <button
                    uiButton="secondary"
                    type="button"
                    (click)="addBlock('paragraph')"
                    [disabled]="doc.blocks.length >= cat.limits.blocks"
                  >
                    Add text
                  </button>
                  <button
                    uiButton="secondary"
                    type="button"
                    (click)="addBlock('box')"
                    [disabled]="doc.blocks.length >= cat.limits.blocks"
                  >
                    Add box
                  </button>
                  <button
                    uiButton="secondary"
                    type="button"
                    (click)="addBlock('repeat')"
                    [disabled]="doc.blocks.length >= cat.limits.blocks"
                  >
                    Add table
                  </button>
                  <button
                    uiButton="secondary"
                    type="button"
                    (click)="addBlock('pageBreak')"
                    [disabled]="doc.blocks.length >= cat.limits.blocks"
                  >
                    Add page break
                  </button>
                </div>
              </fieldset>
            }
            <details #sourceDetails [open]="!composition">
              <summary>Composition JSON · import and diagnostics</summary>
              <label
                >Editable source JSON<textarea
                  #sourceInput
                  uiControl
                  name="notice-source"
                  rows="18"
                  [ngModel]="sourceJson"
                  (ngModelChange)="setSource($event)"
                  [disabled]="!canEdit"
                  spellcheck="false"
                ></textarea>
              </label>
              <p class="muted">
                Source stays intact. Save locally to validate it and open the
                editing controls.
              </p>
            </details>
          </div>
          <aside
            class="proof"
            [hidden]="view !== 'preview'"
            aria-label="Paginated notice proof"
          >
            <phms-notice-proof-viewer
              [proof]="exactProof ? proof : null"
              [disabled]="busy"
              (downloadPdf)="exportPdf()"
              (checkWording)="checkWording()"
            />
          </aside>
        </div>
      } @else {
        <p class="scope-empty">
          Choose an authorized scope to open the editor.
        </p>
      }
    }
  </div>
</section>
